// Week-banded developmental schedule for the first ~8 months — wake windows,
// feed cadence, nap counts, night-stretch goals, and when a real bedtime exists.
// Hand-authored from established newborn sleep & feeding guidance; this table is
// the engine's deterministic source of truth.
//
// Notes:
// - Night stretching is "= week# hours" from week 4 on, and ONLY once the
//   pediatrician confirms healthy weight gain. The gating happens in the UI.
// - Wake windows widen 30-60 min (newborn) → 90-150 min (5+ months).
// - Daytime feed cadence widens ~2-3 hr → ~3-4 hr.
// - `hasBedtime` is false until ~8 weeks: before then a baby has no fixed
//   bedtime — they wake every 2-3 hours around the clock, day and night.
// - `nightSleepGoalHrs` is the *total* night-sleep target — the literal arc
//   toward ~7pm-7am (12h) by 5 months. Null until a bedtime exists.
// - Daytime sleep is never capped during supply-building (weeks 0-7): capping
//   sleep then would undercut feeding. A soft cap starts ~week 8.

export type Phase =
  | "supply-building"
  | "foundation"
  | "stretching"
  | "regression-prep"
  | "regression-active"
  | "pre-sleep-training";

export type AgeBand = {
  weeksMin: number;
  weeksMax: number;
  label: string;
  phase: Phase;
  phaseLabel: string;
  wakeWindowMin: number;
  wakeWindowMax: number;
  feedIntervalHrsMin: number;
  feedIntervalHrsMax: number;
  feedsPerDayMin: number;
  feedsPerDayMax: number;
  napsPerDayMin: number;
  napsPerDayMax: number;
  napCapMin: number | null;
  totalDaytimeSleepCapHrs: number | null;
  /** "Stretch each week" target for the single longest night stretch. From week 4, equals week#. */
  nightStretchTargetHours: number | null;
  /** Total night-sleep target (hrs) — the arc toward ~12h (7pm-7am). Null before a bedtime exists. */
  nightSleepGoalHrs: number | null;
  /** Expected week-over-week gain in the longest night stretch. */
  weeklyStretchGainHrs: number;
  /** Whether a fixed evening bedtime applies yet. False before ~8 weeks. */
  hasBedtime: boolean;
  dreamFeed: "encouraged" | "optional" | "wean" | "maintain" | "off";
  bedtimeEarliest: string; // "HH:MM"
  bedtimeLatest: string;
  swaddleStatus: "full" | "one-arm-out" | "transition" | "off";
  sitBackTimingPreset: "newborn" | "3-4mo";
  cries5sActive: boolean;
  philosophyNote: string;
  milestones: string[];
  troubleshooterVariant: "newborn" | "3-4mo";
};

const BANDS: AgeBand[] = [
  {
    weeksMin: 0, weeksMax: 2,
    label: "Week 0–2",
    phase: "supply-building",
    phaseLabel: "Supply-building phase",
    wakeWindowMin: 30, wakeWindowMax: 60,
    feedIntervalHrsMin: 2, feedIntervalHrsMax: 3,
    feedsPerDayMin: 8, feedsPerDayMax: 12,
    napsPerDayMin: 4, napsPerDayMax: 7,
    napCapMin: null,
    totalDaytimeSleepCapHrs: null,
    nightStretchTargetHours: null,
    nightSleepGoalHrs: null,
    weeklyStretchGainHrs: 0,
    hasBedtime: false,
    dreamFeed: "optional",
    bedtimeEarliest: "19:00", bedtimeLatest: "22:00",
    swaddleStatus: "full",
    sitBackTimingPreset: "newborn",
    cries5sActive: true,
    philosophyNote:
      "These first weeks are about feeding and recovery. Feed on her cues — and at least every 2–3 hours in the day — and rest when she rests. No schedule yet.",
    milestones: ["Regained birth weight", "Pediatrician check-up complete"],
    troubleshooterVariant: "newborn",
  },
  {
    weeksMin: 3, weeksMax: 3,
    label: "Week 3",
    phase: "foundation",
    phaseLabel: "Foundation phase",
    wakeWindowMin: 45, wakeWindowMax: 75,
    feedIntervalHrsMin: 2, feedIntervalHrsMax: 3,
    feedsPerDayMin: 7, feedsPerDayMax: 10,
    napsPerDayMin: 4, napsPerDayMax: 5,
    napCapMin: null,
    totalDaytimeSleepCapHrs: null,
    nightStretchTargetHours: null,
    nightSleepGoalHrs: null,
    weeklyStretchGainHrs: 0,
    hasBedtime: false,
    dreamFeed: "encouraged",
    bedtimeEarliest: "19:00", bedtimeLatest: "22:00",
    swaddleStatus: "full",
    sitBackTimingPreset: "newborn",
    cries5sActive: true,
    philosophyNote:
      "Start a gentle rhythm — a full feed, a little awake time, then sleep. Keep it loose; still no clock-watching.",
    milestones: ["First full nap in bassinet"],
    troubleshooterVariant: "newborn",
  },
  {
    weeksMin: 4, weeksMax: 4,
    label: "Week 4",
    phase: "stretching",
    phaseLabel: "Stretching phase",
    wakeWindowMin: 60, wakeWindowMax: 90,
    feedIntervalHrsMin: 2, feedIntervalHrsMax: 3,
    feedsPerDayMin: 7, feedsPerDayMax: 9,
    napsPerDayMin: 4, napsPerDayMax: 5,
    napCapMin: null,
    totalDaytimeSleepCapHrs: null,
    nightStretchTargetHours: 4,
    nightSleepGoalHrs: null,
    weeklyStretchGainHrs: 1,
    hasBedtime: false,
    dreamFeed: "encouraged",
    bedtimeEarliest: "19:00", bedtimeLatest: "22:00",
    swaddleStatus: "full",
    sitBackTimingPreset: "newborn",
    cries5sActive: true,
    philosophyNote:
      "Once the pediatrician confirms good weight gain, you can let one night stretch run a little longer — aim for a 4-hour stretch. Days stay flexible.",
    milestones: ["Pediatrician cleared night stretching"],
    troubleshooterVariant: "newborn",
  },
  ...stretchingBand(5),
  ...stretchingBand(6),
  ...stretchingBand(7),
  ...stretchingBand(8),
  {
    weeksMin: 9, weeksMax: 11,
    label: "Weeks 9–11",
    phase: "stretching",
    phaseLabel: "Stretching phase",
    wakeWindowMin: 60, wakeWindowMax: 90,
    feedIntervalHrsMin: 2, feedIntervalHrsMax: 3,
    feedsPerDayMin: 6, feedsPerDayMax: 8,
    napsPerDayMin: 3, napsPerDayMax: 4,
    napCapMin: null,
    totalDaytimeSleepCapHrs: 5.5,
    nightStretchTargetHours: 9,
    nightSleepGoalHrs: 10,
    weeklyStretchGainHrs: 0.5,
    hasBedtime: true,
    dreamFeed: "wean",
    bedtimeEarliest: "19:00", bedtimeLatest: "20:00",
    swaddleStatus: "full",
    sitBackTimingPreset: "newborn",
    cries5sActive: true,
    philosophyNote:
      "Night sleep is consolidating. A consistent early bedtime and a short, calm wind-down routine help it along.",
    milestones: ["Sleeping 8+ hr stretches"],
    troubleshooterVariant: "newborn",
  },
  {
    weeksMin: 12, weeksMax: 15,
    label: "Weeks 12–15",
    phase: "regression-prep",
    phaseLabel: "Months 3–4 transition",
    // Broad age-appropriate envelope. The scheduler blends this prior with the
    // baby's recent observed wake windows; this range is a guardrail, not a target.
    wakeWindowMin: 75, wakeWindowMax: 150,
    feedIntervalHrsMin: 2, feedIntervalHrsMax: 4,
    feedsPerDayMin: 5, feedsPerDayMax: 7,
    napsPerDayMin: 3, napsPerDayMax: 4,
    napCapMin: 120,
    totalDaytimeSleepCapHrs: 5,
    nightStretchTargetHours: 10,
    nightSleepGoalHrs: 10,
    weeklyStretchGainHrs: 0.25,
    hasBedtime: true,
    dreamFeed: "maintain",
    bedtimeEarliest: "19:00", bedtimeLatest: "20:00",
    swaddleStatus: "transition",
    sitBackTimingPreset: "3-4mo",
    cries5sActive: false,
    philosophyNote:
      "Sleep is reorganizing — expect a few rougher weeks; it's a normal, permanent upgrade. Use wake windows as a flexible guide, follow her cues, keep bedtime steady, and cap daytime sleep around 5 hours.",
    milestones: [
      "First time falling asleep independently",
      "Recognizes bedtime routine as a cue",
    ],
    troubleshooterVariant: "3-4mo",
  },
  {
    weeksMin: 16, weeksMax: 20,
    label: "Weeks 16–20",
    phase: "regression-active",
    phaseLabel: "Active 4-month phase",
    wakeWindowMin: 90, wakeWindowMax: 120,
    feedIntervalHrsMin: 2, feedIntervalHrsMax: 4,
    feedsPerDayMin: 5, feedsPerDayMax: 6,
    napsPerDayMin: 3, napsPerDayMax: 4,
    napCapMin: 120,
    totalDaytimeSleepCapHrs: 5,
    nightStretchTargetHours: 10,
    nightSleepGoalHrs: 11,
    weeklyStretchGainHrs: 0.25,
    hasBedtime: true,
    dreamFeed: "maintain",
    bedtimeEarliest: "19:00", bedtimeLatest: "20:00",
    swaddleStatus: "off",
    sitBackTimingPreset: "3-4mo",
    cries5sActive: false,
    philosophyNote:
      "Aim for one night feed at most — use her recent longest stretch as the bar before offering one. A consistent ~7pm bedtime now anchors the whole day.",
    milestones: [
      "Down to one night feeding",
      "Out of swaddle (both arms free)",
      "Has a lovey",
    ],
    troubleshooterVariant: "3-4mo",
  },
  {
    weeksMin: 21, weeksMax: 999,
    label: "5+ months",
    phase: "pre-sleep-training",
    phaseLabel: "Ready for a full night",
    wakeWindowMin: 90, wakeWindowMax: 150,
    feedIntervalHrsMin: 3, feedIntervalHrsMax: 4,
    feedsPerDayMin: 4, feedsPerDayMax: 6,
    napsPerDayMin: 2, napsPerDayMax: 3,
    napCapMin: 120,
    totalDaytimeSleepCapHrs: 4,
    nightStretchTargetHours: 11,
    nightSleepGoalHrs: 11.5,
    weeklyStretchGainHrs: 0,
    hasBedtime: true,
    dreamFeed: "off",
    bedtimeEarliest: "19:00", bedtimeLatest: "20:00",
    swaddleStatus: "off",
    sitBackTimingPreset: "3-4mo",
    cries5sActive: false,
    philosophyNote:
      "She's ready for a solid 11–12 hour night — roughly 7pm to 7am. Consistent bedtime, wake time, and naps are what lock it in.",
    milestones: ["Sleeping through the night (10–12 hr)"],
    troubleshooterVariant: "3-4mo",
  },
];

function stretchingBand(week: number): AgeBand[] {
  return [
    {
      weeksMin: week, weeksMax: week,
      label: `Week ${week}`,
      phase: "stretching",
      phaseLabel: "Stretching phase",
      wakeWindowMin: 60, wakeWindowMax: 90,
      feedIntervalHrsMin: 2, feedIntervalHrsMax: 3,
      feedsPerDayMin: 6, feedsPerDayMax: 8,
      napsPerDayMin: 4, napsPerDayMax: 5,
      napCapMin: null,
      totalDaytimeSleepCapHrs: week >= 8 ? 5.5 : null,
      nightStretchTargetHours: week,
      nightSleepGoalHrs: week >= 8 ? 9 : null,
      weeklyStretchGainHrs: 1,
      hasBedtime: week >= 8,
      dreamFeed: "encouraged",
      bedtimeEarliest: "19:00", bedtimeLatest: "21:00",
      swaddleStatus: "full",
      sitBackTimingPreset: "newborn",
      cries5sActive: true,
      philosophyNote:
        week >= 8
          ? `Aim for one ${week}-hour stretch at night. When she stirs, give her a few minutes to resettle before offering a feed — and start anchoring a consistent bedtime.`
          : `Aim for one ${week}-hour stretch at night. When she stirs, give her a few minutes to resettle before offering a feed.`,
      milestones: [],
      troubleshooterVariant: "newborn",
    },
  ];
}

export function bandForWeeks(weeks: number): AgeBand {
  return BANDS.find((b) => weeks >= b.weeksMin && weeks <= b.weeksMax) ?? BANDS[BANDS.length - 1];
}

export function allBands(): AgeBand[] {
  return BANDS;
}
